import { ContentCopy, Download, MoreTime, Stop } from "@mui/icons-material";
import { Button, ButtonGroup, Sheet, Snackbar, Stack, Typography } from "@mui/joy";
import { isPast } from "date-fns";
import { format } from "date-fns-tz";
import QRCode from "qrcode";
import React from "react";

import { useChallengeQuery, useUpdateChallengeMutation } from "../hooks/challenges";
import { useCurrentVenueQuery, useCurrentVenueUuid } from "../hooks/venues";

export default function ChallengeTools({ challengeUuid }: { challengeUuid: string }) {
  const venueUuid = useCurrentVenueUuid();
  const challengeQuery = useChallengeQuery(challengeUuid, venueUuid);
  const { data: venue } = useCurrentVenueQuery();
  const challenge = challengeQuery.data;

  const updateChallengeMutation = useUpdateChallengeMutation(challengeUuid, venueUuid);

  const [showMutationSnackbar, setShowMutationSnackbar] = React.useState(false);

  const shareUrl = challenge?.shareUrl;

  const onCopyShareLinkClick = () => {
    shareUrl && navigator.clipboard.writeText(shareUrl);
  };

  const onDownloadQRCode = async () => {
    if (!shareUrl) {
      return;
    }
    try {
      const canvas = document.createElement("canvas");
      await QRCode.toCanvas(canvas, shareUrl, {
        width: 512,
        margin: 2,
        color: {
          dark: "#000000",
          light: "#FFFFFF",
        },
      });

      // Convert canvas to blob and download
      canvas.toBlob((blob) => {
        if (blob) {
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = `challenge-${challengeUuid}-qr.png`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }
      }, "image/png");
    } catch (error) {
      console.error("Error generating QR code:", error);
      // Could add error handling/snackbar here if needed
    }
  };

  const onAdd15Mins = () => {
    if (!challenge || !challenge.endTime) {
      return;
    }
    const newEndTime = Math.max(
      new Date(challenge.endTime).getTime() + 15 * 60 * 1000,
      Date.now() + 15 * 60 * 1000,
    );
    updateChallengeMutation.mutate(
      {
        endTime: new Date(newEndTime).toISOString(),
      },
      {
        onSettled: () => {
          setShowMutationSnackbar(true);
        },
      },
    );
  };

  const onEndNow = () => {
    updateChallengeMutation.mutate(
      {
        endTime: "NOW",
      },
      {
        onSettled: () => {
          setShowMutationSnackbar(true);
        },
      },
    );
  };

  const canEndNow = challenge && (challenge.endTime === null || !isPast(challenge.endTime));
  const canAdd15Mins = challenge && challenge.endTime !== null;

  return (
    <Stack
      sx={{
        backgroundColor: "neutral.200",
        padding: 2,
        flex: 1,
        overflow: "auto",
      }}
      spacing={2}
    >
      <Sheet sx={{ padding: 2, borderRadius: 16 }}>
        <Typography level="h3" marginBottom={1}>
          Share Challenge
        </Typography>
        <Typography level="body-sm">
          Generate a QR code or copy a link to promote the challenge
        </Typography>
        <ButtonGroup sx={{ marginTop: 1 }} spacing={1}>
          <Button
            color="primary"
            variant="solid"
            startDecorator={<Download />}
            onClick={onDownloadQRCode}
          >
            Download QR code
          </Button>
          <Button startDecorator={<ContentCopy />} onClick={onCopyShareLinkClick}>
            Copy link
          </Button>
        </ButtonGroup>
      </Sheet>
      <Sheet sx={{ padding: 2, borderRadius: 16 }}>
        <Typography level="h3" marginBottom={1}>
          End time
        </Typography>
        <Typography level="body-sm">
          {challenge?.endTime
            ? "Extend the challenge by 15 minutes or end it early"
            : "This challenge has no end time. You can end it manually."}
        </Typography>
        <ButtonGroup sx={{ marginTop: 1 }} spacing={1}>
          <Button
            color="primary"
            variant="solid"
            startDecorator={<MoreTime />}
            onClick={onAdd15Mins}
            loading={
              updateChallengeMutation.isPending &&
              updateChallengeMutation.variables?.endTime !== "NOW"
            }
            loadingPosition="start"
            disabled={updateChallengeMutation.isPending || !canAdd15Mins}
          >
            Add 15 minutes
          </Button>
          <Button
            color="danger"
            variant="outlined"
            // hack to fix mui separator bug
            sx={
              !updateChallengeMutation.isPending && canEndNow
                ? {
                    borderLeftColor: "var(--joy-palette-danger-outlinedBorder)!important",
                  }
                : {}
            }
            startDecorator={<Stop />}
            onClick={onEndNow}
            loading={
              updateChallengeMutation.isPending &&
              updateChallengeMutation.variables?.endTime === "NOW"
            }
            loadingPosition="start"
            disabled={updateChallengeMutation.isPending || !canEndNow}
          >
            End Now
          </Button>
        </ButtonGroup>
        <Snackbar
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          open={showMutationSnackbar}
          onClose={() => setShowMutationSnackbar(false)}
          color={updateChallengeMutation.isSuccess ? "success" : "danger"}
          autoHideDuration={10000}
          endDecorator={
            <Button
              onClick={() => setShowMutationSnackbar(false)}
              size="sm"
              variant="soft"
              color={updateChallengeMutation.isSuccess ? "success" : "danger"}
            >
              Dismiss
            </Button>
          }
        >
          {challenge && updateChallengeMutation.isSuccess ? (
            challenge.endTime ? (
              <>
                Challenge end time is now{" "}
                {format(challenge.endTime, "HH:mm", {
                  timeZone: venue?.timezone,
                })}
              </>
            ) : (
              "Challenge has been ended successfully"
            )
          ) : null}
          {updateChallengeMutation.isError
            ? "End time update failed. Please try again or contact support."
            : null}
        </Snackbar>
      </Sheet>
    </Stack>
  );
}
